import React, { forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import Dropzone from 'react-dropzone';
import clone from 'ramda/src/clone';
import uniq from 'ramda/src/uniq';
import cx from 'classnames';
import Button from '@components/@core/AnimatedButton';
import Icon from '@components/@core/Icon';

const Upload = (
    {
        className = '',
        accept = '',
        title = 'Upload',
        label = '',
        icon = 'fas fa-upload',
        error = '',
        size = 16,
        color = '#969faa',
        source = [],
        hiddenFile = false,
        multiple = false,
        position = 'center',
        type = 'link',
        dataCy = '',
        onDrop = () => {},
        onRead = () => {},
    },
    ref
) => {
    const [files, setFiles] = useState(source);

    // Convert string accept prop to object format for react-dropzone
    const acceptObj = React.useMemo(() => {
        if (!accept || typeof accept !== 'string') return {};
        
        // Split comma-separated values and create object
        return accept.split(',').reduce((obj, type) => {
            const trimmedType = type.trim();
            if (trimmedType) {
                obj[trimmedType] = [];
            }
            return obj;
        }, {});
    }, [accept]);

    const handleDrop = (e) => {
        if (files.length > 0 && multiple) {
            const _file = uniq(files.concat(e));
            onDrop(_file);
            setFiles(_file);
        } else {
            onDrop(e);
            setFiles(e);
        }
        if (e.length > 0) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                onRead(reader.result);
            });
            reader.readAsDataURL(e[0]);
        }
    };

    const handleRemove = (idx) => {
        if (idx < files.length) {
            const items = clone(files);
            items.splice(idx, 1);
            onDrop(items);
            setFiles(items);
        }
    };

    useImperativeHandle(ref, () => ({
        remove: (index) => {
            handleRemove(index);
        },
    }));

    return (
        <Dropzone className="drag-drop" onDrop={handleDrop} accept={acceptObj} multiple={multiple} noClick noKeyboard>
            {({ getRootProps, getInputProps, open }) => (
                <div className={cx('ds-upload', className)} data-cy={dataCy}>
                    {label && <p className="ds-upload-label my-0">{label}</p>}
                    <div {...getRootProps({ className: 'dropzone' })}>
                        <input {...getInputProps()} />
                        {(multiple || !files.length || !title) && (
                            <Button className={`${position} upload-button`} type={type} onClick={open}>
                                <div className={cx(error)}>
                                    {icon && <Icon name={`${icon} me-2`} size={size} color={color} />}
                                    {title && !title.includes('.') && (
                                        <span className={`dsl-b${size} buttont-text`} style={{ color }}>
                                            {title}
                                        </span>
                                    )}
                                </div>
                            </Button>
                        )}
                    </div>
                    {!hiddenFile &&
                        title &&
                        files.map((item, index) => (
                            <div className="d-flex ai-center my-1" key={`attachment-${index}`}>
                                <a className={`dsl-b${size} ms-2`} style={{ color }} href={item.preview || URL.createObjectURL(item)} target="_blank">
                                    {item.name}
                                </a>
                                <Icon name="fas fa-trash-can-alt cursor-pointer ms-3" color="#969faa" size={12} onClick={() => handleRemove(index)} />
                            </div>
                        ))}
                    {error && <span className="dsl-r12 error">{error}</span>}
                </div>
            )}
        </Dropzone>
    );
};

const UploadComponent = forwardRef(Upload);

UploadComponent.propTypes = {
    accept: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    title: PropTypes.string.isRequired,
    label: PropTypes.string,
    icon: PropTypes.string,
    error: PropTypes.string,
    className: PropTypes.string,
    size: PropTypes.number,
    color: PropTypes.string,
    hiddenFile: PropTypes.bool,
    multiple: PropTypes.bool,
    position: PropTypes.oneOf(['left', 'center', 'right']),
    type: PropTypes.oneOf(['high', 'medium', 'low', 'link']),
    onDrop: PropTypes.func,
    onRead: PropTypes.func,
};

export default UploadComponent;
